import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import AdminServiceRequestsTab from '@/epics/serviceManagement/pages/allServiceRequests/AdminServiceRequestsTab';
import MyAssignedTasksTab from '@/epics/serviceManagement/pages/allServiceRequests/MyAssignedTasksTab';
import { MY_REQUESTS_LABELS } from '@/epics/serviceManagement/constants/labels';
import MyContactRequestsTab from './MyContactRequestsTab';
import { MY_CONTACT_REQUESTS_LABELS } from '../../constants/labels';
import { useUserRole } from '@/shared/hooks/useUserRole';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';
import { TabToggle } from '@shared/components/TabToggle';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import {
  ColumnCustomizationPopup,
  type ColumnOption,
} from '@shared/components/ColumnCustomizationPopup';
import { Tooltip } from '@shared/components/Tooltip';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { useContactRequestList } from '../../hooks/useContactRequestList';
import { getContactRequestStatusConfig } from '../../hooks/useViewContactRequest';
import type { ContactRequestListItem } from '../../types/contactRequestTypes';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import noDataContactRequestAnimation from '../../../../../public/assets/lottie/NoDataScreenContactRequest.json';

type ActiveTab =
  | 'serwisowe'
  | 'kontaktowe'
  | 'moje-zadania'
  | 'moje-kontaktowe';

const TABS: { id: ActiveTab; label: string }[] = [
  { id: 'serwisowe', label: 'Zgłoszenia serwisowe' },
  { id: 'kontaktowe', label: 'Zgłoszenia kontaktowe' },
  { id: 'moje-zadania', label: MY_REQUESTS_LABELS.TAB_MY_TASKS },
  { id: 'moje-kontaktowe', label: MY_CONTACT_REQUESTS_LABELS.TAB_LABEL },
];

function StatusBadge({ status }: { status: string }) {
  const cfg = getContactRequestStatusConfig(status);
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-lg whitespace-nowrap"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.1px',
      }}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg.text }}
      />
      {cfg.label}
    </span>
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function ClientAvatar({
  name,
  url,
}: {
  name: string;
  url?: string | null | undefined;
}) {
  const [imgError, setImgError] = useState(false);
  const showImage = !!url && !imgError;
  return (
    <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0 overflow-hidden">
      {showImage ? (
        <img
          src={url}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="text-[#223746] text-base font-medium">
          {getInitials(name)}
        </span>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}/${month}/${day}, ${hours}:${minutes}:${seconds}`;
}

const PLACEHOLDER_NA = '—';

const COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'contact_request_id', label: 'ID zgłoszenia', disabled: true },
  { key: 'client_name', label: 'Nazwa klienta' },
  { key: 'client_user_id', label: 'ID klienta', disabled: true },
  { key: 'client_email', label: 'Adres e-mail' },
  { key: 'client_phone', label: 'Numer telefonu' },
  { key: 'subject', label: 'Temat' },
  { key: 'created_at', label: 'Data utworzenia' },
  { key: 'assigned_to', label: 'Przydzielono do', disabled: true },
  { key: 'status', label: 'Status', disabled: true },
];

const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = Object.fromEntries(
  COLUMN_OPTIONS.map((o) => [o.key, true])
);

const COLUMNS_URL_PARAM = 'cols';
const DEFAULT_COLS_STRING = COLUMN_OPTIONS.map((o) => o.key).join(',');

function parseColsParam(value: string | null): Record<string, boolean> | null {
  if (!value) {
    return null;
  }
  const validKeys = new Set(COLUMN_OPTIONS.map((o) => o.key));
  const visible = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validKeys.has(s));
  if (visible.length === 0) {
    return null;
  }
  const result: Record<string, boolean> = {};
  COLUMN_OPTIONS.forEach((o) => {
    result[o.key] = visible.includes(o.key);
  });
  return result;
}

function buildColsParam(visibleKeys: Record<string, boolean>): string {
  return COLUMN_OPTIONS.filter((o) => visibleKeys[o.key] !== false)
    .map((o) => o.key)
    .join(',');
}

// ─── Kontaktowe tab — isolated so useContactRequestList only runs when mounted ─

function KontaktoweTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<
    Record<string, boolean>
  >(() => ({ ...DEFAULT_VISIBLE_COLUMNS }));

  const navigate = useNavigate();
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const prevOpenMenuRowIdRef = useRef<string | null>(null);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const columnContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SPACE_BELOW_FOR_MENU = 70;

  const { privileges } = useUserPrivileges();

  const colsParam = searchParams.get(COLUMNS_URL_PARAM);
  useEffect(() => {
    const parsed = parseColsParam(colsParam);
    setVisibleColumnKeys(parsed ?? { ...DEFAULT_VISIBLE_COLUMNS });
  }, [colsParam]);

  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    setSearch,
    isSearchClearing,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    accessError,
  } = useContactRequestList();

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setStatusOptions(appliedFilters.statusOptions);
    setDateRange(appliedFilters.dateRange);
    setIsFilterOpen(false);
  }, [appliedFilters, setStatusOptions, setDateRange]);

  const handleCloseColumnPopup = () => setIsColumnPopupOpen(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isFilterOpen &&
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
      if (
        isColumnPopupOpen &&
        columnContainerRef.current &&
        !columnContainerRef.current.contains(target)
      ) {
        handleCloseColumnPopup();
      }
      if (
        openMenuRowId !== null &&
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(target)
      ) {
        setOpenMenuRowId(null);
      }
    };
    if (isFilterOpen || isColumnPopupOpen || openMenuRowId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen, isColumnPopupOpen, openMenuRowId, handleCloseFilter]);

  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      const anchor = menuAnchorRef.current;
      if (anchor) {
        const firstItem =
          anchor.querySelector<HTMLElement>('[role="menuitem"]');
        if (firstItem) {
          requestAnimationFrame(() => firstItem.focus());
        }
      }
    } else {
      const hadOpen = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpen && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const next = { ...visibleColumnKeys, [key]: visible };
    setVisibleColumnKeys(next);
    const colsString = buildColsParam(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (colsString === DEFAULT_COLS_STRING) {
          p.delete(COLUMNS_URL_PARAM);
        } else {
          p.set(COLUMNS_URL_PARAM, colsString);
        }
        return p;
      },
      { replace: true }
    );
  };

  const columns: Column<ContactRequestListItem>[] = useMemo(
    () => [
      {
        key: 'contact_request_id',
        header: 'ID zgłoszenia',
        minWidth: '150px',
        render: (row) => (
          <Tooltip content={row.contact_request_id} useContentWidth>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.contact_request_id}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'client_name',
        header: 'Nazwa klienta',
        minWidth: '160px',
        render: (row) => (
          <div className="flex items-center gap-3 min-w-0">
            <ClientAvatar name={row.client_name} url={row.client_profile_url} />
            <span className="flex-1 min-w-0 overflow-hidden">
              <Tooltip content={row.client_name} useContentWidth>
                <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                  {row.client_name}
                </span>
              </Tooltip>
            </span>
          </div>
        ),
      },
      {
        key: 'client_user_id',
        header: 'ID klienta',
        minWidth: '170px',
        render: (row) => (
          <Tooltip content={row.client_user_id} useContentWidth>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.client_user_id}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'client_email',
        header: 'Adres e-mail',
        minWidth: '180px',
        maxWidth: '250px',
        render: (row) => (
          <Tooltip content={row.client_email}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.client_email}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'client_phone',
        header: 'Numer telefonu',
        minWidth: '160px',
        render: (row) => (
          <Tooltip content={row.client_phone || PLACEHOLDER_NA} useContentWidth>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.client_phone || PLACEHOLDER_NA}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'subject',
        header: 'Temat',
        minWidth: '160px',
        maxWidth: '200px',
        render: (row) => (
          <Tooltip content={row.subject}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.subject}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'created_at',
        header: 'Data utworzenia',
        minWidth: '180px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        key: 'assigned_to',
        header: 'Przydzielono do',
        minWidth: '130px',
        maxWidth: '160px',
        render: (row) => {
          const displayName = row.assigned_user_name ?? PLACEHOLDER_NA;
          return (
            <Tooltip content={displayName}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {displayName}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: '220px',
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'actions',
        header: 'Akcje',
        width: '60px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const btnRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    setMenuOpensUpward(
                      visibleBottom - btnRect.bottom < MIN_SPACE_BELOW_FOR_MENU
                    );
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla zgłoszenia ${row.contact_request_id}`}
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      return;
                    }
                    const anchor = menuAnchorRef.current;
                    if (!anchor) {
                      return;
                    }
                    const items =
                      anchor.querySelectorAll<HTMLElement>('[role="menuitem"]');
                    const lastItem = items[items.length - 1];
                    if (
                      lastItem &&
                      document.activeElement === lastItem &&
                      !e.shiftKey
                    ) {
                      setOpenMenuRowId(null);
                    }
                  }}
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: 'Podgląd',
                        disabled: !privileges.contactRequestView,
                        disabledTooltip:
                          'Nie masz uprawnień do wykonania tej czynności',
                        disabledTooltipSide: 'left',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(
                          `/Zgłoszenia/Podgląd-zgłoszenia-kontaktowego/${row.id}`
                        );
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate, privileges]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key === 'actions' || visibleColumnKeys[col.key] !== false
      ),
    [columns, visibleColumnKeys]
  );

  if (accessError) {
    return <ErrorPage403 />;
  }

  const filterCount =
    appliedFilters.statusOptions.filter((o) => o.checked).length +
    (appliedFilters.dateRange.startDate ? 1 : 0) +
    (appliedFilters.dateRange.endDate ? 1 : 0);

  const hasAppliedSearch = search.trim() !== '';
  const hasAppliedFilters =
    appliedFilters.statusOptions.some((o) => o.checked) ||
    !!appliedFilters.dateRange.startDate ||
    !!appliedFilters.dateRange.endDate;
  const hasAppliedFiltersOrSearch = hasAppliedSearch || hasAppliedFilters;

  const noResultsSubtitle =
    hasAppliedSearch && hasAppliedFilters
      ? 'Spróbuj zmienić kryteria wyszukiwania lub filtry.'
      : hasAppliedSearch
        ? 'Spróbuj zmienić kryteria wyszukiwania.'
        : 'Spróbuj zmienić ustawienia filtrów.';

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    page === 1 &&
    items.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasAppliedFiltersOrSearch;

  return (
    <>
      {/* Search + filter toolbar */}
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Szukaj po ID zgłoszenia"
          />
          <div className="flex items-center gap-2">
            <div
              ref={filterContainerRef}
              className={`dropdown ${isFilterOpen ? 'show' : ''}`}
              style={{ position: 'relative' }}
            >
              <FilterBar
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                isOpen={isFilterOpen}
                filterCount={filterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={handleCloseFilter}
                statusOptions={statusOptions}
                onStatusChange={setStatusOptions}
                showDateRange={true}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                onApply={handleApplyFiltersAndClose}
                onClear={handleClearFilters}
              />
            </div>
            <div ref={columnContainerRef} className="relative">
              <button
                type="button"
                data-testid="column-customization-button"
                onClick={() => {
                  handleCloseFilter();
                  setIsColumnPopupOpen((prev) => !prev);
                }}
                className="flex items-center justify-center h-10 w-10 rounded-lg border border-[#DCDDE0] cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label="Kolumny"
                aria-expanded={isColumnPopupOpen}
                aria-haspopup="dialog"
              >
                <ViewColumnsIcon
                  className="h-6 w-6 text-on-surface-variant shrink-0"
                  aria-hidden
                />
              </button>
              <ColumnCustomizationPopup
                isOpen={isColumnPopupOpen}
                onClose={handleCloseColumnPopup}
                columnOptions={COLUMN_OPTIONS}
                visibleKeys={visibleColumnKeys}
                onVisibilityChange={handleColumnVisibilityChange}
                disabledTooltipText="Ta kolumna jest wymagana"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table / Empty states */}
      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {items.length === 0 && !loading && !isSearchClearing ? (
          hasNoData ? (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <div className="mr-[130px]">
                <LottieAnimation
                  animationData={noDataContactRequestAnimation}
                  loop
                  width={277}
                  aspectRatio="1 / 1"
                />
              </div>
              <div className=" max-w-full flex flex-col items-center gap-1">
                <div className="w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                  Brak zgłoszeń kontaktowych.
                </div>
                <div className="w-full text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                  Zgłoszenia pojawią się tutaj po ich utworzeniu.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-full gap-3">
              <LottieAnimation
                animationData={noResultsAnimation}
                loop
                width="clamp(160px, 18vw, 360px)"
                aspectRatio="432 / 350"
              />
              <div className="w-[548px] max-w-full flex flex-col items-center gap-1">
                <div className="w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                  Brak wyników.
                </div>
                <div className="w-full text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                  {noResultsSubtitle}
                </div>
              </div>
            </div>
          )
        ) : (
          <Table
            columns={visibleColumns}
            data={items}
            loading={loading}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
          />
        )}
      </div>

      {/* Pagination */}
      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Page shell — tab state only, mounts the right tab ───────────────────────

export default function ContactRequestList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  // "Moje zadania" is for ADMIN only — hidden for SUPER_ADMIN. Treat the
  // loading/unknown state as "not allowed" so the tab never flashes for a
  // super admin.
  const canViewMyTasks = role === 'ADMIN';
  const canSR = privileges.serviceRequestList;
  const canCR = privileges.contactRequestList;

  // First available tab in priority order — used when the requested tab is
  // unavailable due to privilege or role.
  const defaultTab: ActiveTab = canSR
    ? 'serwisowe'
    : canCR
      ? 'kontaktowe'
      : 'moje-zadania';

  const tabsWithPrivileges = TABS.map((tab) => {
    if (tab.id === 'serwisowe' && !canSR) {
      return {
        ...tab,
        disabled: true,
        disabledTooltip: 'Nie masz uprawnień do wykonania tej czynności',
      };
    }
    if (tab.id === 'kontaktowe' && !canCR) {
      return {
        ...tab,
        disabled: true,
        disabledTooltip: 'Nie masz uprawnień do wykonania tej czynności',
      };
    }
    // "My Contact Requests" hits the same contact-request privilege on the
    // backend (my-tasks 403s without it), so disable it alongside kontaktowe.
    if (tab.id === 'moje-kontaktowe' && !canCR) {
      return {
        ...tab,
        disabled: true,
        disabledTooltip: 'Nie masz uprawnień do wykonania tej czynności',
      };
    }
    return tab;
  });

  const visibleTabs = canViewMyTasks
    ? tabsWithPrivileges
    : tabsWithPrivileges.filter(
        (t) => t.id !== 'moje-zadania' && t.id !== 'moje-kontaktowe'
      );

  const urlTab = searchParams.get('tab');
  const resolvedTab: ActiveTab =
    urlTab === 'kontaktowe'
      ? 'kontaktowe'
      : urlTab === 'moje-zadania'
        ? 'moje-zadania'
        : urlTab === 'moje-kontaktowe'
          ? 'moje-kontaktowe'
          : 'serwisowe';

  const activeTab: ActiveTab = (() => {
    if (
      (resolvedTab === 'moje-zadania' || resolvedTab === 'moje-kontaktowe') &&
      !canViewMyTasks
    ) {
      return defaultTab;
    }
    if (resolvedTab === 'serwisowe' && !isPrivilegesLoading && !canSR) {
      return defaultTab;
    }
    if (resolvedTab === 'kontaktowe' && !isPrivilegesLoading && !canCR) {
      return defaultTab;
    }
    return resolvedTab;
  })();

  // Always write the active tab into the URL so the history stack entry always
  // has a tab param — this ensures breadcrumb back-navigation returns to the
  // correct tab regardless of how the user arrived at this page. Also rewrites
  // a disallowed tab (super admin moje-zadania, or privilege-blocked SR/CR).
  useEffect(() => {
    const tab = searchParams.get('tab');
    const disallowedMyTasks =
      (tab === 'moje-zadania' || tab === 'moje-kontaktowe') &&
      !roleLoading &&
      role !== 'ADMIN';
    const disabledSR = tab === 'serwisowe' && !isPrivilegesLoading && !canSR;
    const disabledCR = tab === 'kontaktowe' && !isPrivilegesLoading && !canCR;
    if (!tab || disallowedMyTasks || disabledSR || disabledCR) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', defaultTab);
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    role,
    roleLoading,
    canSR,
    canCR,
    isPrivilegesLoading,
    defaultTab,
  ]);

  const handleTabChange = (tab: ActiveTab) => {
    const next = new URLSearchParams();
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      <div className="flex items-center justify-between shrink-0">
        <div className="text-[#181A1B] text-[22px] font-normal leading-7">
          Zgłoszenia
        </div>
      </div>

      <div className="shrink-0">
        <TabToggle
          tabs={visibleTabs}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel="Zgłoszenia"
          fitContent
          overflow
        />
      </div>

      {activeTab === 'serwisowe' && canSR ? (
        <AdminServiceRequestsTab />
      ) : activeTab === 'moje-zadania' ? (
        <MyAssignedTasksTab />
      ) : activeTab === 'moje-kontaktowe' ? (
        <MyContactRequestsTab />
      ) : activeTab === 'kontaktowe' && canCR ? (
        <KontaktoweTab />
      ) : null}
    </div>
  );
}
